import React from 'react';
import { Link } from 'react-router-dom';
import '../../styles/ServiceDetail.css';

function AIStrategy() {
  return (
    <div className="service-detail">
      <section className="service-hero">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/services">Services</Link> / AI Strategy Consulting
          </div>
          <h1>AI Strategy Consulting</h1>
          <p className="hero-subtitle">
            Navigate your AI transformation journey with expert guidance and proven frameworks
          </p>
        </div>
      </section>

      <section className="service-overview">
        <div className="container">
          <div className="overview-content">
            <h2>Strategic AI Transformation for SMEs</h2>
            <p>
              Adopting AI without a clear strategy can be costly and ineffective. Our AI Strategy 
              Consulting service helps you assess your readiness, identify high-impact opportunities, 
              and create a practical roadmap that delivers measurable ROI. We translate AI capabilities 
              into business outcomes, ensuring every investment drives real value.
            </p>
          </div>

          <div className="key-benefits">
            <h3>Key Benefits</h3>
            <div className="benefits-list">
              <div className="benefit">
                <span className="benefit-icon">🎯</span>
                <div>
                  <h4>Clear Direction</h4>
                  <p>Eliminate uncertainty with data-driven decision making</p>
                </div>
              </div>
              <div className="benefit">
                <span className="benefit-icon">💡</span>
                <div>
                  <h4>Quick Wins</h4>
                  <p>Identify and prioritize high-impact, low-effort opportunities</p>
                </div>
              </div>
              <div className="benefit">
                <span className="benefit-icon">📊</span>
                <div>
                  <h4>Measurable ROI</h4>
                  <p>Quantify expected returns before making investments</p>
                </div>
              </div>
              <div className="benefit">
                <span className="benefit-icon">🚀</span>
                <div>
                  <h4>Faster Adoption</h4>
                  <p>Reduce risk and accelerate time to value</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="what-we-offer">
        <div className="container">
          <h2>Our Consulting Services</h2>
          
          <div className="automation-areas">
            <div className="area-card">
              <h3>🔍 AI Readiness Assessment</h3>
              <ul>
                <li>Current technology stack evaluation</li>
                <li>Data maturity and quality assessment</li>
                <li>Team skills and capability analysis</li>
                <li>Infrastructure readiness review</li>
                <li>Process automation potential mapping</li>
                <li>Change management readiness evaluation</li>
              </ul>
            </div>

            <div className="area-card">
              <h3>🗺️ Automation Roadmap Development</h3>
              <ul>
                <li>Strategic priority identification</li>
                <li>Phased implementation planning</li>
                <li>Quick wins vs. long-term initiatives</li>
                <li>Resource allocation planning</li>
                <li>Risk mitigation strategies</li>
                <li>Timeline and milestone definition</li>
              </ul>
            </div>

            <div className="area-card">
              <h3>💰 ROI Estimation & Business Case</h3>
              <ul>
                <li>Cost-benefit analysis</li>
                <li>Productivity gain projections</li>
                <li>Cost savings calculations</li>
                <li>Revenue impact modeling</li>
                <li>Payback period estimation</li>
                <li>Executive presentation materials</li>
              </ul>
            </div>

            <div className="area-card">
              <h3>🛠️ Technology Stack Recommendations</h3>
              <ul>
                <li>AI/ML platform selection guidance</li>
                <li>LLM provider comparison (ChatGPT, Claude, etc.)</li>
                <li>RPA tool recommendations</li>
                <li>Integration architecture design</li>
                <li>Vendor evaluation criteria</li>
                <li>Build vs. buy analysis</li>
              </ul>
            </div>

            <div className="area-card">
              <h3>📋 Implementation Planning</h3>
              <ul>
                <li>Detailed project plans and timelines</li>
                <li>Resource requirement identification</li>
                <li>Pilot program design</li>
                <li>Success metrics definition</li>
                <li>Governance framework establishment</li>
                <li>Scaling strategies</li>
              </ul>
            </div>

            <div className="area-card">
              <h3>👥 Change Management & Training</h3>
              <ul>
                <li>Stakeholder communication strategies</li>
                <li>Training needs assessment</li>
                <li>Adoption acceleration tactics</li>
                <li>Resistance management</li>
                <li>Culture transformation guidance</li>
                <li>Knowledge transfer planning</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="consulting-approach">
        <div className="container">
          <h2>Our Consulting Methodology</h2>
          <div className="approach-grid">
            <div className="approach-item">
              <h4>Business-First Mindset</h4>
              <p>We start with your business objectives, not technology. Every recommendation is tied to measurable business outcomes.</p>
            </div>
            <div className="approach-item">
              <h4>Pragmatic Approach</h4>
              <p>We focus on practical, achievable solutions that work within your constraints and deliver quick value.</p>
            </div>
            <div className="approach-item">
              <h4>Data-Driven Insights</h4>
              <p>Our recommendations are backed by analysis, benchmarks, and proven case studies from similar SMEs.</p>
            </div>
            <div className="approach-item">
              <h4>Collaborative Process</h4>
              <p>We work closely with your team, building internal capabilities while delivering strategic guidance.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="implementation-process">
        <div className="container">
          <h2>Typical Engagement Timeline</h2>
          <div className="process-timeline">
            <div className="timeline-item">
              <div className="timeline-marker">Week 1-2</div>
              <div className="timeline-content">
                <h4>Discovery & Assessment</h4>
                <p>Stakeholder interviews, process analysis, and data review to understand current state</p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-marker">Week 3</div>
              <div className="timeline-content">
                <h4>Opportunity Identification</h4>
                <p>Map automation opportunities, estimate impact, and prioritize based on effort vs. value</p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-marker">Week 4</div>
              <div className="timeline-content">
                <h4>Strategy Development</h4>
                <p>Create detailed roadmap with phased approach, timelines, and resource requirements</p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-marker">Week 5</div>
              <div className="timeline-content">
                <h4>ROI Modeling</h4>
                <p>Build financial models showing costs, benefits, and payback periods for each initiative</p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-marker">Week 6</div>
              <div className="timeline-content">
                <h4>Presentation & Handoff</h4>
                <p>Deliver comprehensive strategy document and present recommendations to leadership</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="deliverables">
        <div className="container">
          <h2>What You'll Receive</h2>
          <div className="deliverables-grid">
            <div className="deliverable-card">
              <h3>📊 Assessment Report</h3>
              <p>Comprehensive analysis of your AI readiness across technology, data, people, and processes</p>
            </div>
            <div className="deliverable-card">
              <h3>🗺️ Strategic Roadmap</h3>
              <p>Phased implementation plan with clear priorities, timelines, and dependencies</p>
            </div>
            <div className="deliverable-card">
              <h3>💼 Business Case</h3>
              <p>Detailed ROI analysis with cost-benefit modeling for executive decision making</p>
            </div>
            <div className="deliverable-card">
              <h3>🛠️ Technology Blueprint</h3>
              <p>Recommended tools, platforms, and architecture for your AI initiatives</p>
            </div>
            <div className="deliverable-card">
              <h3>📋 Implementation Guide</h3>
              <p>Step-by-step playbook for executing your first automation projects</p>
            </div>
            <div className="deliverable-card">
              <h3>📈 Success Metrics</h3>
              <p>KPIs and measurement framework to track progress and demonstrate value</p>
            </div>
          </div>
        </div>
      </section>

      <section className="who-its-for">
        <div className="container">
          <h2>Who This Is For</h2>
          <div className="audience-list">
            <div className="audience-item">
              <h4>✓ SMEs exploring AI adoption</h4>
              <p>Companies wanting to understand how AI can benefit their specific business</p>
            </div>
            <div className="audience-item">
              <h4>✓ Organizations with failed AI pilots</h4>
              <p>Businesses that tried AI but didn't achieve expected results</p>
            </div>
            <div className="audience-item">
              <h4>✓ Companies facing budget constraints</h4>
              <p>Teams needing to justify AI investments with clear ROI</p>
            </div>
            <div className="audience-item">
              <h4>✓ Businesses scaling operations</h4>
              <p>Growing companies looking to automate before adding headcount</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>Ready to Build Your AI Strategy?</h2>
          <p>Let's create a practical roadmap that delivers measurable business value</p>
          <Link to="/contact" className="btn btn-primary">Book Your Strategy Session</Link>
        </div>
      </section>
    </div>
  );
}

export default AIStrategy;
